import { createContext, useContext, useMemo, useState } from 'react';
import { type ApplicationAgreementDataFragment } from '../../../../api/fragments/ApplicationAgreementData';
import { type KycFieldSpecsFragment } from '../../../../api/fragments/KYCFieldSpecs';
import { CustomerKind } from '../../../../api/types';
import { type AgreementValues } from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';

export type EventKycState = {
    active: KycFieldSpecsFragment[];
    applicant: KycFieldSpecsFragment[];
    guarantor: KycFieldSpecsFragment[];
    corporate: KycFieldSpecsFragment[];
};

export type EventAgreementState = {
    values: AgreementValues;
    data: ApplicationAgreementDataFragment[];
};

type EventJourneyKycAndAgreementContextProps = {
    kycState: EventKycState;
    setKycState: React.Dispatch<React.SetStateAction<EventKycState>>;

    agreementState: EventAgreementState;
    setAgreementState: React.Dispatch<React.SetStateAction<EventAgreementState>>;

    isCorporateCustomer: boolean;
    setIsCorporate: React.Dispatch<React.SetStateAction<boolean>>;

    prefill: boolean;
    setPrefill: React.Dispatch<React.SetStateAction<boolean>>;

    customerKind: CustomerKind;
    hasGuarantorPreset: boolean;
};

const EventJourneyKycAndAgreementContext = createContext<EventJourneyKycAndAgreementContextProps>(null);

type EventJourneyKycAndAgreementProviderProps = {
    customerKind?: CustomerKind;
    initialActiveKycFields?: KycFieldSpecsFragment[];
    initialActiveAgreements?: ApplicationAgreementDataFragment[];
    children: React.ReactNode;
};

export const EventJourneyKycAndAgreementProvider = ({
    children,
    customerKind,
    initialActiveAgreements,
    initialActiveKycFields,
}: EventJourneyKycAndAgreementProviderProps) => {
    const [isCorporateCustomer, setIsCorporate] = useState(false);
    const [prefill, setPrefill] = useState(false);

    const [agreementState, setAgreementState] = useState<EventAgreementState>({
        values: {},
        data: initialActiveAgreements ?? [],
    });

    const [kycState, setKycState] = useState<EventKycState>({
        applicant: [],
        active: initialActiveKycFields ?? [],
        guarantor: [],
        corporate: [],
    });

    const values = useMemo((): EventJourneyKycAndAgreementContextProps => {
        const applicant = isCorporateCustomer ? CustomerKind.Corporate : CustomerKind.Local;
        const currentCustomerKind = customerKind ?? applicant;

        return {
            kycState,
            setKycState,

            agreementState,
            setAgreementState,

            prefill,
            setPrefill,

            isCorporateCustomer,
            setIsCorporate,

            customerKind: currentCustomerKind,
            hasGuarantorPreset: currentCustomerKind !== CustomerKind.Guarantor && kycState.guarantor.length > 0,
        };
    }, [agreementState, customerKind, isCorporateCustomer, kycState, prefill]);

    return (
        <EventJourneyKycAndAgreementContext.Provider value={values}>
            {children}
        </EventJourneyKycAndAgreementContext.Provider>
    );
};

export const useEventJourneyKycAndAgreementContext = () => {
    const context = useContext(EventJourneyKycAndAgreementContext);

    if (!context) {
        throw new Error('EventJourneyKycAndAgreementContext is missing');
    }

    return context;
};
